const destinationKey = "editor:loginDestination";
const destinationLifetime = 30 * 60 * 1000;

/**
 * Create a copy of the provided URL, omitting `tokenId` or `sat` query parameters.
 */
function withoutTokens(url: URL): URL {
    const clean = new URL(url.href);
    // Keep the original encoding: the editor's query parser treats literal + differently
    // from %20, so serialising all parameters with URLSearchParams would change filters.
    clean.search = clean.search.slice(1).split("&").filter(part => {
        const parameter = new URLSearchParams(part);
        return !parameter.has("tokenId") && !parameter.has("sat");
    }).join("&");
    return clean;
}

/**
 * Get the bit of a URL from the pathname onwards.
 */
function relativeUrl(url: URL): string {
    return url.pathname + url.search + url.hash;
}

export function navigateToLogin(): void {
    const destination = withoutTokens(new URL(location.href));
    try {
        // A failed callback at / must not overwrite the original candidate page.
        if (relativeUrl(destination) !== "/" || !sessionStorage.getItem(destinationKey)) {
            sessionStorage.setItem(destinationKey, JSON.stringify({
                path: relativeUrl(destination),
                savedAt: Date.now(),
            }));
        }
    } catch {
        // Login must still work when browser storage is unavailable.
    }
    location.assign("https://accounts.mevitae.com/?service=editor");
}

/**
 * Call after the handover token has been consumed, before reading document filters, to restore them
 * from before the login attempt.
 */
export function restoreLoginDestination(incomingUrl: URL): void {
    let destination = withoutTokens(incomingUrl);
    try {
        const stored = sessionStorage.getItem(destinationKey);
        sessionStorage.removeItem(destinationKey);
        if (stored && relativeUrl(destination) === "/") {
            const { path, savedAt } = JSON.parse(stored);
            const age = Date.now() - savedAt;
            if (typeof path === "string" && typeof savedAt === "number" &&
                Number.isFinite(age) && age >= 0 && age < destinationLifetime) {
                const savedUrl = new URL(path, incomingUrl.origin);
                if (savedUrl.origin === incomingUrl.origin && !savedUrl.username && !savedUrl.password) {
                    destination = withoutTokens(savedUrl);
                }
            }
        }
    } catch {
        // Ignore unavailable storage and malformed or outdated saved destinations.
    }
    // Also preserves an explicit incoming hash, which the shared token helper removes.
    history.replaceState(history.state, "", relativeUrl(destination));
}
